import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Gavel, ExternalLink, Ban, Lock } from "lucide-react";
import { CATEGORY_META } from "../constants/content";
import { focusIncident } from "../lib/focusIncident";
import { isLocked, lock } from "../lib/locks";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const THRESHOLD = 5;
const VETO_THRESHOLD = 3;

const DocketItem = ({ sub, onChange, onClosed }) => {
  const [busy, setBusy] = useState(false);
  const [testified, setTestified] = useState(() => isLocked(`docket:${sub.id}`));
  const cat = CATEGORY_META[sub.category] || CATEGORY_META.all;
  const pct = Math.min(100, (sub.seconds / THRESHOLD) * 100);
  const vetoPct = Math.min(100, ((sub.vetoes || 0) / VETO_THRESHOLD) * 100);

  const vote = async (action) => {
    if (busy) return;
    if (testified) {
      toast.error("YOU ALREADY TESTIFIED ON THIS ONE.");
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/submissions/${sub.id}/${action}`);
      lock(`docket:${sub.id}`);
      setTestified(true);
      if (r.data.status === "promoted") {
        toast.success("PROMOTED TO THE TIMELINE. JUSTICE.");
        onClosed(r.data);
        setTimeout(() => focusIncident(r.data.id), 600);
      } else if (r.data.status === "tossed") {
        toast.error("CASE DISMISSED. TOSSED FROM THE DOCKET.");
        onClosed(r.data);
      } else {
        onChange(r.data);
      }
    } catch (e) {
      toast.error("Could not record that. Case already closed?");
    } finally {
      setBusy(false);
    }
  };

  const voteBtn = "brutal-press inline-flex items-center gap-1.5 font-mono text-[11px] font-bold uppercase px-3 py-1.5 brutal-border disabled:opacity-60";

  return (
    <li className="bg-black brutal-border p-4 space-y-3" style={{ borderWidth: 3 }} data-testid={`docket-item-${sub.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className={`font-mono text-[10px] px-2 py-1 brutal-border ${cat.color}`} style={{ borderWidth: 2 }}>{cat.label}</span>
        <span className="font-mono text-[11px] text-white/60 uppercase">{sub.date}</span>
        <span className="font-mono text-[11px] text-[#FF003C] uppercase ml-auto">Cringe {sub.cringe}/10</span>
      </div>
      <h4 className="heading text-xl text-white leading-none">{sub.title}</h4>
      <p className="text-xs text-white/70 leading-relaxed">{sub.description}</p>
      <div className="flex flex-wrap items-center gap-3 font-mono text-[10px] text-white/50">
        <span>by {sub.submitted_by || "anonymous_witness"}</span>
        {sub.evidence_url && (
          <a href={sub.evidence_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#FFEA00] underline decoration-dotted">
            evidence <ExternalLink size={10} strokeWidth={3} />
          </a>
        )}
        {testified && (
          <span className="inline-flex items-center gap-1 text-white/70 ml-auto" data-testid={`docket-testified-${sub.id}`}>
            <Lock size={10} strokeWidth={3} /> you testified
          </span>
        )}
      </div>
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <div className="flex-1 h-3 bg-[#222] brutal-border" style={{ borderWidth: 2 }}>
            <div className="h-full bg-[#00FF00] transition-[width] duration-300" style={{ width: `${pct}%` }} />
          </div>
          <span className="font-mono text-[11px] text-white w-8 text-right" data-testid={`docket-seconds-${sub.id}`}>{sub.seconds}/{THRESHOLD}</span>
          <button onClick={() => vote("second")} disabled={busy} data-testid={`docket-second-${sub.id}`} className={`${voteBtn} bg-[#00FF00] text-black`} style={{ borderWidth: 3, boxShadow: "3px 3px 0 0 #fff" }}>
            <Gavel size={13} strokeWidth={3} /> Second
          </button>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex-1 h-3 bg-[#222] brutal-border" style={{ borderWidth: 2 }}>
            <div className="h-full bg-[#FF003C] transition-[width] duration-300" style={{ width: `${vetoPct}%` }} />
          </div>
          <span className="font-mono text-[11px] text-white w-8 text-right" data-testid={`docket-vetoes-${sub.id}`}>{sub.vetoes || 0}/{VETO_THRESHOLD}</span>
          <button onClick={() => vote("veto")} disabled={busy} data-testid={`docket-veto-${sub.id}`} className={`${voteBtn} bg-[#FF003C] text-white`} style={{ borderWidth: 3, boxShadow: "3px 3px 0 0 #fff" }}>
            <Ban size={13} strokeWidth={3} /> Cap
          </button>
        </div>
      </div>
    </li>
  );
};

export const Docket = ({ items, setItems }) => {
  const update = (s) => setItems((xs) => xs.map((x) => (x.id === s.id ? s : x)));
  const remove = (s) => setItems((xs) => xs.filter((x) => x.id !== s.id));

  return (
    <div className="space-y-4" data-testid="docket">
      <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-[#00FF00]">
        <Gavel size={14} strokeWidth={3} /> The Docket · {items.length} awaiting trial
      </div>
      <p className="font-mono text-[10px] text-white/40">
        5 seconds → promoted to the Timeline. 3 caps → tossed as bogus. One vote per witness.
      </p>
      {items.length === 0 ? (
        <div className="font-mono text-sm text-white/50 brutal-border p-4" style={{ borderWidth: 3 }} data-testid="docket-empty">
          Docket is empty. Suspiciously quiet. File something.
        </div>
      ) : (
        <ul className="space-y-4 max-h-[720px] overflow-y-auto pr-1">
          {items.map((s) => <DocketItem key={s.id} sub={s} onChange={update} onClosed={remove} />)}
        </ul>
      )}
    </div>
  );
};
